// src/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config/config';
import ProfileDetails from '../components/ProfileDetails';
import EditProfileForm from '../components/forms/EditProfileForm';
import LoadingModal from '../components/modals/LoadingModal';
import AdminLayout from '../components/admin/AdminLayout'; // Add this import if not present

const ProfilePage = () => {
  const { currentUser, isAuthenticated, checkAuthStatus } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Detect if this is the admin area by checking the URL path
  const isAdmin = window.location.pathname.startsWith('/admin');

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isAuthenticated || !currentUser) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Get the token directly from currentUser instead of localStorage
        const token = currentUser.token;
        
        if (!token) {
          // If we have current user from context, use that
          setUserProfile(currentUser);
          setLoading(false);
          return;
        }
        
        // Use the correct API path and Authorization header
        const response = await axios.get(
          `${config.BASE_URL}/api/profile/user-profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        

        if (response.data && response.data.success) {
          setUserProfile(response.data.user);
        } else {
          setUserProfile(currentUser); // Fallback to context user
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        // Always use currentUser as fallback
        setUserProfile(currentUser);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, isAuthenticated]);

  // Handle profile update
  const handleProfileUpdate = async (updatedProfile) => {
    setUserProfile(updatedProfile);
    setIsEditing(false);
    await checkAuthStatus();
  };

  if (!isAuthenticated) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <h4>Authentication Required</h4>
          <p>Please log in to view your profile.</p>
          <button className="button" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingModal show={true} message="Loading profile..." />;
  }

  // Wrap with AdminLayout if in admin area, else render as normal
  const content = (
    <div className="container pt-5 mb-5" style={{ paddingTop: '120px' }}>
      {error && !userProfile && (
        <div className="alert alert-danger">
          <h4>Error Loading Profile</h4>
          <p>{error}</p>
        </div>
      )}

      {userProfile && (
        <>
          {isEditing ? (
            <EditProfileForm 
              user={userProfile} 
              onCancel={() => setIsEditing(false)} 
              updateUser={handleProfileUpdate} 
            />
          ) : (
            <ProfileDetails 
              user={userProfile} 
              onEdit={() => setIsEditing(true)} 
            />
          )}
        </>
      )}
    </div>
  );

  return isAdmin ? <AdminLayout>{content}</AdminLayout> : content;
};

export default ProfilePage;
